import {
  QueryClient,
  QueryState,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { generatePath, useMatch } from 'react-router-dom'
import { FeatureFlags } from '@opallabs/brown-dwarf'
import {
  IANATimezoneIdentifier,
  UserRoles,
  WeekStartsOn,
} from '@opallabs/concepts'
import { globalErrorStore } from '@opallabs/hooks'
import { OpalRoutes, useBypassAuthentication } from '@opallabs/routing'
import { OpalApiResponseDocument, OpalApiV2Component } from '../../../schema'
import {
  findResourceOfType,
  isResourceOfType,
} from '../../../schema/validation/resourceObject'
import { users } from '../../../v2/users'
import { globalQueryKeys } from '../../queryKeys'

type Workspace = {
  id: string
  logoSrc: string | undefined
  name: string
  slug: string
  startOfWeek: WeekStartsOn
  timezone: IANATimezoneIdentifier
  uuid: string
}

export type AuthenticatedUser = {
  approvalCount: number
  currentWorkspace: Workspace | undefined
  email: string
  firstName: string
  fullName: string
  id: string
  integrations: OpalApiV2Component<'dispatch_provider'>[]
  isAdminForCurrentWorkspace: boolean
  lastName: string
  opalId: string
  primaryWorkspace: Workspace | undefined
  routeWorkspace: Workspace | undefined
  uuid: string
  workspaces: Workspace[]
  avatarAsset?: OpalApiV2Component<'asset_reference'>
}

const mapRole = (role?: string): UserRoles =>
  ({
    admin: UserRoles.Admin,
    limited: UserRoles.Limited,
    opal_admin: UserRoles.OpalAdmin,
    owner: UserRoles.Owner,
    publisher: UserRoles.Publisher,
    user: UserRoles.User,
    view_only: UserRoles.Viewer,
  })[role || ''] || UserRoles.Viewer

type UserDocument = OpalApiResponseDocument<'ReadMeV2', 200>

const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'

/**
 * The me query refetches every 30 seconds (see useOpalVersionCheck). A
 * transient failure, for example during a monolith-web restart, must not
 * unmount the app through an error boundary while cached user data is still
 * available. An error is unrecoverable only when there is no cached data to
 * fall back on, or when it is a 401 and the session expiration flow has to
 * take over.
 */
const isUnrecoverableMeError = (
  error: unknown,
  query: { state: Pick<QueryState, 'data'> }
): boolean =>
  query.state.data === undefined ||
  (error instanceof AxiosError && error.response?.status === 401)

export const buildMeQuery = () => ({
  queryKey: globalQueryKeys.authenticatedUser(),
  useErrorBoundary: isUnrecoverableMeError,
  queryFn: async () => {
    const response = await users.readMe({
      query: {
        include: [
          'avatar_asset_reference',
          'brand_admin_users',
          'brands',
          'brands.dispatch_providers',
          'brands.logo',
          'moments_setting',
          'opal',
          'opal.plan',
          'primary_brand',
          'profile',
          'role',
        ],
        expose: {
          meta: { workspace_approval_counts: {} },
        },
      },
    })

    if (!IS_DEVELOPMENT) {
      const opalId = response.data.relationships.opal.data.id
      const opal = response.included?.find(
        findResourceOfType('opal', 'V2', opalId)
      )

      if (
        opal &&
        opal.meta.app_version !== window.OPAL_CLIENT_APPLICATION_VERSION
      ) {
        globalErrorStore.getState().setErrorCode('opal_version_mismatch')
      }
    }

    return response
  },
})

/**
 * @note If you’re working in Storybook and want to override data from this hook or a hook that calls it — for example,
 * to control a feature flag — you need a decorator that wraps your story in
 * `<BypassAuthenticationContext.Provider value={false}>`, and you need to add `readControlledMeV2Success` to the `msw`
 * `handlers. Auth checks are bypassed by default in Storybook, so the hook (and your override) won’t fire without the
 * decorator.
 */
export const useMe = <T = UserDocument>({
  select,
}: Partial<
  Pick<UseQueryOptions<UserDocument, Error, T>, 'select'>
>): UseQueryResult<T, unknown> => {
  const bypassAuthentication = useBypassAuthentication()

  return useQuery<UserDocument, Error, T>({
    ...buildMeQuery(),
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    select,
    enabled: !bypassAuthentication,
  })
}

export const useAuthenticatedUser = ({
  routeWorkspaceSlug,
}: {
  routeWorkspaceSlug?: string
}) => {
  const calendarViewMatch = useMatch({
    path: OpalRoutes.Calendar,
    end: true,
  })

  return useMe({
    select: (document): AuthenticatedUser => {
      const { workspaces, currentWorkspace, primaryWorkspace, routeWorkspace } =
        parseWorkspaceData(document, routeWorkspaceSlug)

      /**
       * If the user doesn't have a `routeWorkspace`, then they don't have
       * access to the workspace they are attempting to navigate to.
       * Redirect them to the Calendar View, where they will be asked to
       * join the workspace.
       */
      if (!routeWorkspace && routeWorkspaceSlug && calendarViewMatch === null) {
        const calendarView = generatePath(OpalRoutes.Calendar, {
          workspaceSlug: routeWorkspaceSlug,
        })
        // Changing workspace requires a hard redirect instead of using
        // react router to softly navigate
        // This helps Backbone views properly manage their stored data
        window.location.assign(calendarView)
      }

      const currentWorkspaceAdminIds =
        document.data.relationships.brand_admin_users?.data.map(
          (user) => user.id
        )

      if (!document.data.attributes.feature_flags) {
        throw new Error(
          'Unable to retrieve feature flags from authenticated user'
        )
      }

      const role = mapRole(document.data.attributes.role_name)

      // `included` holds the providers of every workspace the user belongs
      // to, so keep only the ones the workspace in view has provisioned.
      const integrationsWorkspace = routeWorkspace ?? currentWorkspace
      const workspaceProviderIds = new Set(
        document.included
          ?.filter(isResourceOfType('brand', 'V2'))
          .find((brand) => brand.id === integrationsWorkspace?.id)
          ?.relationships.dispatch_providers.data.map(
            (provider) => provider.id
          ) ?? []
      )

      const isAdminForCurrentWorkspace =
        ([UserRoles.Admin, UserRoles.OpalAdmin].includes(role) ||
          currentWorkspaceAdminIds?.includes(document.data.id)) ??
        false

      return {
        approvalCount:
          typeof document.data.meta?.workspace_approval_counts === 'string'
            ? parseInt(document.data.meta?.workspace_approval_counts)
            : 0,
        currentWorkspace,
        id: document.data.id,
        uuid: document.data.attributes.uuid,
        email: document.data.attributes.email,
        firstName: document.data.attributes.first_name,
        lastName: document.data.attributes.last_name,
        fullName: document.data.attributes.full_name,
        opalId: document.data.relationships.opal.data.id,
        primaryWorkspace,
        routeWorkspace,
        isAdminForCurrentWorkspace,
        workspaces: workspaces ?? [],
        integrations:
          document.included
            ?.filter(isResourceOfType('dispatch_provider', 'V2'))
            .filter((provider) => workspaceProviderIds.has(provider.id)) ?? [],
        avatarAsset: document.included?.find(
          findResourceOfType(
            'asset_reference',
            'V2',
            document.data.relationships.avatar_asset_reference?.data?.id
          )
        ),
      }
    },
  })
}

export const getAuthenticatedUserWorkspace = async (
  queryClient: QueryClient,
  workspaceSlug: string | undefined
) => {
  const meQuery = buildMeQuery()
  const authData = await queryClient.ensureQueryData(meQuery)

  const { currentWorkspace, primaryWorkspace, routeWorkspace } =
    parseWorkspaceData(authData, workspaceSlug)
  return routeWorkspace ?? currentWorkspace ?? primaryWorkspace ?? null
}

export const parseWorkspaceData = (
  document: OpalApiResponseDocument<'ReadMeV2', 200>,
  routeWorkspaceSlug?: string
) => {
  const assets = document.included?.filter(
    isResourceOfType('asset_reference', 'V2')
  )

  const workspaces: Workspace[] | undefined = document.included
    ?.filter(isResourceOfType('brand', 'V2'))
    .map((brand) => {
      const logoId = brand.relationships.logo.data?.id
      const brandLogo = assets?.find((asset) => asset.id === logoId)

      return {
        id: brand.id,
        logoSrc: brandLogo?.attributes.preview_url ?? undefined,
        name: brand.attributes.name,
        slug: brand.attributes.slug,
        startOfWeek: brand.attributes.start_of_week,
        timezone: brand.attributes.timezone,
        uuid: brand.attributes.uuid,
      }
    })

  const momentsSettingId = document.data.relationships.moments_setting?.data?.id

  const currentWorkspaceId = document.included
    ?.filter(isResourceOfType('moments_setting', 'V2'))
    .find((include) => include.id === momentsSettingId)
    ?.attributes.current_brand_id

  const primaryWorkspaceId = document.data.relationships.brands?.data[0]?.id

  const currentWorkspace = workspaces?.find(
    (workspace) =>
      workspace.id ===
      (typeof currentWorkspaceId === 'number'
        ? currentWorkspaceId?.toString()
        : undefined)
  )

  const primaryWorkspace = workspaces?.find(
    (workspace) => workspace.id === primaryWorkspaceId
  )

  const routeWorkspace = workspaces?.find(
    (workspace) => workspace.slug === routeWorkspaceSlug
  )

  return {
    currentWorkspace,
    primaryWorkspace,
    routeWorkspace,
    workspaces,
  }
}

export const useFeatureFlags = () => {
  return useMe({
    select: (document) => {
      return document.data.attributes.feature_flags as FeatureFlags
    },
  })
}

export const useUserRole = () => {
  return useMe({
    select: (document) => {
      const role = mapRole(document.data.attributes.role_name)

      const isRestrictedUser =
        role === UserRoles.Limited || role === UserRoles.Viewer

      const roleIs = (...testRoles: Array<UserRoles>) =>
        testRoles.includes(role)
      const roleIsNot = (...testRoles: Array<UserRoles>) =>
        !testRoles.includes(role)

      return {
        /**
         * UserRoles.Limited or UserRoles.Viewer
         */
        isRestrictedUser,
        role,
        /**
         * Tests if authenticated user's role matches any of the given UserRoles
         */
        roleIs,
        /**
         * Tests if authenticated user's role does not match any of the given UserRoles
         */
        roleIsNot,
      }
    },
  })
}

export const useOpal = () => {
  return useMe({
    select: (document) => {
      return document.included?.find((include) => include.type === 'opal')
    },
  })
}
